export const API_BASE = 'https://cobalt-api.euan-hoole1.workers.dev/api';
export const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
})();

async function fetchAPI(endpoint: string, options: RequestInit = {}) {
  try {
    const url = `${API_BASE}${endpoint}`;
    console.log(`[API] ${options.method || 'GET'} ${url}`);
    
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    console.log(`[API] Response status: ${res.status}`);
    
    if (!res.ok) {
      let errorMessage = `Request failed with status ${res.status}`;
      try {
        const errorData = await res.json();
        errorMessage = errorData.error || errorMessage;
        console.error(`[API] Error response:`, errorData);
      } catch (parseError) {
        console.error(`[API] Could not parse error response`);
      }
      throw new Error(errorMessage);
    }
    
    const data = await res.json();
    console.log(`[API] Success:`, data);
    return data;
  } catch (error: any) {
    // Network errors or other failures
    if (error.message === 'Failed to fetch') {
      console.error(`[API] Network error - could not reach server`);
      throw new Error('Cannot connect to server. Please check your connection.');
    }
    console.error(`[API] Error:`, error.message);
    throw error;
  }
}

const buildSessionQuery = (options?: { fields?: string[]; full?: boolean }) => {
  const params = new URLSearchParams();
  if (options?.full) params.set('full', '1');
  if (options?.fields && options.fields.length > 0) {
    params.set('fields', options.fields.join(','));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const api = {
  auth: {
    register: (email: string, password: string, profileData = {}) =>
      fetchAPI('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, profileData }) }),
    login: (email: string, password: string) =>
      fetchAPI('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    updateProfile: (user: any, password?: string) =>
      fetchAPI('/auth/profile', { method: 'PUT', body: JSON.stringify({ user, password }) }),
    getUserByEmail: (email: string) =>
      fetchAPI(`/auth/user/${encodeURIComponent(email)}`).catch(() => null),
  },
  sessions: {
    get: (userId: string, options?: { fields?: string[]; full?: boolean }) =>
      fetchAPI(`/sessions/${userId}${buildSessionQuery(options)}`),
    getById: (userId: string, sessionId: string, options?: { fields?: string[]; full?: boolean }) =>
      fetchAPI(`/sessions/${userId}/${sessionId}${buildSessionQuery(options)}`),
    create: (session: any) => fetchAPI('/sessions', { method: 'POST', body: JSON.stringify(session) }),
    update: (sessionId: string, session: any) =>
      fetchAPI(`/sessions/${sessionId}`, { method: 'PUT', body: JSON.stringify(session) }),
    delete: (userId: string, sessionId: string) =>
      fetchAPI(`/sessions/${userId}/${sessionId}`, { method: 'DELETE' }),
  },
  markEvents: {
    get: (userId: string) => fetchAPI(`/mark-events/${userId}`),
    create: (event: any) => fetchAPI('/mark-events', { method: 'POST', body: JSON.stringify(event) }),
  },
  links: {
    getRequests: (email: string) => fetchAPI(`/links/requests/${encodeURIComponent(email)}`),
    sendRequest: (fromUser: any, toEmail: string, subject: string) =>
      fetchAPI('/links/request', { method: 'POST', body: JSON.stringify({ fromUser, toEmail, subject }) }),
    respond: (requestId: string, accept: boolean, acceptingUser: any) =>
      fetchAPI('/links/respond', { method: 'POST', body: JSON.stringify({ requestId, accept, acceptingUser }) }),
    getConnections: (userId: string) => fetchAPI(`/links/connections/${userId}`),
    removeConnection: (userId: string, targetUserId: string) =>
      fetchAPI(`/links/connection/${userId}/${targetUserId}`, { method: 'DELETE' }),
  },
  invite: {
    generate: (user: any, subjectScope: string) =>
      fetchAPI('/invite/generate', { method: 'POST', body: JSON.stringify({ user, subjectScope }) }),
    preview: (code: string) =>
      fetchAPI(`/invite/preview/${encodeURIComponent(code)}`).catch((e) => ({ error: e.message })),
    accept: (code: string, acceptingUser: any) =>
      fetchAPI('/invite/accept', { method: 'POST', body: JSON.stringify({ code, acceptingUser }) }),
    getMyCodes: (userId: string) => fetchAPI(`/invite/my-codes/${userId}`),
  },
  cache: {
    get: async (cacheKey: string) => {
      try {
        return await fetchAPI(`/cache/${encodeURIComponent(cacheKey)}`);
      } catch {
        return null; // Cache miss
      }
    },
    set: (cacheKey: string, cacheType: string, subject: string, topic: string, response: any, difficulty?: string, ttlHours: number = 24) =>
      fetchAPI('/cache', { 
        method: 'POST', 
        body: JSON.stringify({ cacheKey, cacheType, subject, topic, difficulty, response, ttlHours }) 
      }).catch(() => null), // Silently fail cache writes
  },
  admin: {
    getMetrics: (email: string, password: string) => {
      const token = btoa(`${email}:${password}`);
      return fetchAPI(`/admin/metrics`, {
        headers: { Authorization: `Basic ${token}` }
      });
    },
  },
  share: {
    create: (sessionId: string, userId: string, userName: string, noteTitle: string, subject: string, topicHierarchy: string[], noteData: any) =>
      fetchAPI('/share/create', {
        method: 'POST',
        body: JSON.stringify({ sessionId, userId, userName, noteTitle, subject, topicHierarchy, noteData }),
      }),
    preview: (token: string) =>
      fetchAPI(`/share/preview/${encodeURIComponent(token)}`).catch((e) => ({ error: e.message })),
    accept: (token: string, acceptingUserId: string) =>
      fetchAPI('/share/accept', {
        method: 'POST',
        body: JSON.stringify({ token, acceptingUserId }),
      }),
    revoke: (token: string, userId: string) =>
      fetchAPI(`/share/${encodeURIComponent(token)}`, {
        method: 'DELETE',
        body: JSON.stringify({ userId }),
      }),
  },
  ai: {
    // Use cached spec context - 75% cheaper than sending specs every time
    // Enhanced with Vectorize for topic-specific spec content
    generateCached: (
      message: string,
      history: { role: string; parts: { text: string }[] }[],
      config?: Record<string, unknown>,
      subject?: string,
      subjects?: string[],
      boards?: Record<string, string>,
      topic?: string,
      examBoard?: string,
      level?: string
    ) =>
      fetchAPI('/ai/generate-cached', {
        method: 'POST',
        body: JSON.stringify({ message, history, config, subject, subjects, boards, topic, examBoard, level }),
      }),
    getCacheStatus: () => fetchAPI('/ai/cache-status'),
  },
  analytics: {
    track: (event: { eventType: string; eventName: string; eventData?: Record<string, any>; subject?: string; page?: string }) =>
      fetchAPI('/analytics/track', {
        method: 'POST',
        body: JSON.stringify(event),
      }).catch(() => null), // Silently fail analytics
    trackBatch: (events: { eventType: string; eventName: string; eventData?: Record<string, any>; subject?: string; page?: string }[]) =>
      fetchAPI('/analytics/track-batch', {
        method: 'POST',
        body: JSON.stringify({ events }),
      }).catch(() => null), // Silently fail analytics
  },
};
